import React, { useState, useEffect, useCallback } from 'react';
import { Download, ExternalLink, RefreshCw, Film, Clock, CheckCircle, XCircle, Loader2, Share2, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  pending:   { icon: <Loader2 className="h-3 w-3 animate-spin" />, text: 'Processing', cls: 'bg-amber-100 text-amber-700' },
  completed: { icon: <CheckCircle className="h-3 w-3" />,           text: 'Ready',     cls: 'bg-green-100 text-green-700' },
  failed:    { icon: <XCircle className="h-3 w-3" />,              text: 'Failed',    cls: 'bg-red-100 text-red-700' },
};

const TYPE_LABELS = {
  video_agent:   { label: 'Video Agent',   color: 'bg-violet-100 text-violet-700' },
  avatar_video:  { label: 'Avatar Video',  color: 'bg-blue-100 text-blue-700' },
  translate:     { label: 'Translated',    color: 'bg-emerald-100 text-emerald-700' },
  photo_avatar:  { label: 'Photo Avatar',  color: 'bg-pink-100 text-pink-700' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffH = diffMs / 3600000;
  if (diffH < 1) return `${Math.floor(diffMs / 60000)}m ago`;
  if (diffH < 24) return `${Math.floor(diffH)}h ago`;
  return d.toLocaleDateString();
};

const VideoHistory = ({ videoType, accentColor = 'violet', onSelectVideo }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/heygen/my-videos${videoType ? `?video_type=${videoType}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(res.data.videos || []);
    } catch (_) {}
    setLoading(false);
  }, [videoType]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center gap-2 text-xs text-gray-400 py-3">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your videos...
    </div>
  );

  if (videos.length === 0) return null;

  const handleDownload = (url, title) => {
    if (!url) { toast.error('Video URL not available yet'); return; }
    const a = document.createElement('a');
    a.href = url; a.download = `${title || 'video'}.mp4`; a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleShare = async (recordId, title) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/heygen/share-video/${recordId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const shareUrl = `${window.location.origin}/shared-video/${res.data.share_token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Share link copied to clipboard!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create share link');
    }
  };

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Film className={`h-3.5 w-3.5 text-${accentColor}-600`} />
          <span className="text-xs font-semibold text-gray-700">Your Recent Videos</span>
        </div>
        <button onClick={load} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-hide">
        {videos.map(v => {
          const st = STATUS_CONFIG[v.status] || STATUS_CONFIG.pending;
          const tp = TYPE_LABELS[v.type] || { label: v.type, color: 'bg-gray-100 text-gray-600' };
          return (
            <div
              key={v.id}
              data-testid={`video-history-item-${v.id}`}
              className="flex-shrink-0 w-40 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Thumbnail */}
              <div className="h-20 bg-gray-900 relative overflow-hidden flex items-center justify-center">
                {v.thumbnail_url
                  ? <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover opacity-90" loading="lazy" decoding="async"/>
                  : <Film className="h-7 w-7 text-gray-600" />
                }
                {v.status === 'pending' && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 className="h-5 w-5 text-white animate-spin" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-2 space-y-1.5">
                <p className="text-xs font-medium text-gray-800 leading-tight line-clamp-2">{v.title || 'Video'}</p>
                <div className="flex items-center justify-between">
                  <span className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full font-medium ${st.cls}`}>
                    {st.icon} {st.text}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <Clock className="h-2.5 w-2.5" />
                  <span className="text-xs">{fmtDate(v.created_at)}</span>
                </div>

                {/* Actions */}
                {v.status === 'completed' && v.video_url && (
                  <div className="flex gap-1 pt-0.5">
                    <button
                      onClick={() => handleDownload(v.video_url, v.title)}
                      className="flex-1 flex items-center justify-center gap-0.5 bg-gray-900 text-white rounded-lg py-1 text-xs font-medium hover:bg-gray-700"
                      data-testid="history-download-btn"
                    >
                      <Download className="h-2.5 w-2.5" /> Save
                    </button>
                    <button
                      onClick={() => handleShare(v.id, v.title)}
                      className="w-7 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-pink-50 hover:border-pink-300"
                      title="Copy share link"
                      data-testid="history-share-btn"
                    >
                      <Share2 className="h-2.5 w-2.5 text-gray-500" />
                    </button>
                    <a
                      href={v.video_url} target="_blank" rel="noreferrer"
                      className="w-7 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-50"
                      data-testid="history-open-btn"
                    >
                      <ExternalLink className="h-2.5 w-2.5 text-gray-500" />
                    </a>
                  </div>
                )}
                {v.status === 'failed' && (
                  <p className="text-xs text-red-500">Generation failed</p>
                )}
                {v.status === 'pending' && (
                  <p className="text-xs text-amber-600">Still processing...</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VideoHistory;
